import { Checkbox } from "@/app/components/shadcn/checkbox";
import { Button } from "@/app/components/shadcn/button";
import { Trash2 } from "lucide-react";
import QuantitySelector from "../QuantitySelector";
import Image from "next/image";
import Link from "next/link";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { ApiCartItem } from "@/app/lib/features/cart/cart.types";
import { getSoftBadgeColors } from "@/app/utils/badgeColors";
import { trackProductClick } from "@/app/lib/tracking/interactions";
import CountdownTimer from "../CountdownTimer";

interface BasketItemProps {
  item: ApiCartItem;
  isCheckboxLoading?: boolean;
  isQuantityUpdating?: boolean;
  onQuantityChange: (type: "increase" | "decrease") => void;
  onRemove: () => void;
  onToggleSelect?: () => void;
}

const BasketItem = ({
  item,
  isCheckboxLoading = false,
  isQuantityUpdating = false,
  onQuantityChange,
  onRemove,
  onToggleSelect,
}: BasketItemProps) => {
  const {
    name,
    image,
    price,
    total_price,
    old_price,
    variant,
    badges,
    labels,
    quantity,
    is_selected: isSelected,
    slug,
  } = item;

  return (
    <div className="flex gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 rounded-lg">
      <div className="flex items-start gap-2 lg:gap-3 shrink-0">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelect}
          disabled={isCheckboxLoading}
          className="size-4 lg:size-5 mt-1 lg:mt-2"
        />
        <Link
          href={`/${slug}`}
          className="w-16 h-20 lg:w-24 lg:h-32 overflow-hidden rounded-lg bg-gray-100 relative block"
          onClick={() => { if (item.product_id) trackProductClick(item.product_id); }}
        >
          <FallbackImage
            src={image || ""}
            alt={name}
            fill
            className="object-cover"
            fallbackClassName="object-contain opacity-60"
            sizes="(max-width: 768px) 64px, 96px"
            quality={70}
          />
        </Link>
      </div>

      <div className="flex-1 min-w-0 space-y-2 lg:space-y-3">
        <div>
          {badges && badges.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="text-[10px] lg:text-xs font-medium px-2 py-0.5 rounded border text-white"
                  style={{
                    backgroundColor: badge.color_hex || "#DC2626",
                    borderColor: badge.color_hex || "#DC2626",
                  }}
                >
                  {badge.text}
                </span>
              ))}
            </div>
          )}

          <Link href={`/${slug}`} className="hover:underline" onClick={() => { if (item.product_id) trackProductClick(item.product_id); }}>
            <h3 className="font-medium text-gray-900 leading-tight text-sm lg:text-base line-clamp-2">
              {item.brand_name && <span className="font-bold">{item.brand_name} </span>}
              <span>{name}</span>
            </h3>
          </Link>
          {variant && (variant.color || variant.size) && (
            <p className="text-xs lg:text-sm text-gray-500 mt-1">
              {variant.color && `Renk: ${variant.color}`}{" "}
              {variant.size && `| Beden: ${variant.size}`}
            </p>
          )}
          {item.product_code && (
            <p className="text-xs text-gray-400 mt-0.5">{item.product_code}</p>
          )}
        </div>

        {labels && labels.length > 0 && (
          <div className="space-y-2">
            {labels.map((label, index) => {
              const colors = getSoftBadgeColors(label.color_hex);

              return (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 lg:p-3 rounded-lg border"
                  style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.bg,
                  }}
                >
                  {label.icon_full && (
                    <div className="relative w-4 h-4 lg:w-5 lg:h-5 shrink-0">
                      <Image
                        src={label.icon_full}
                        alt={label.type}
                        fill
                        className="object-contain"
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 flex-1">
                    <p
                      className="text-xs lg:text-sm font-medium"
                      style={{ color: colors.text }}
                    >
                      {label.text}
                    </p>
                    {label.flash_sale_ends_at && (
                      <CountdownTimer
                        endTime={label.flash_sale_ends_at}
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="lg:hidden space-y-3">
          <div className="flex items-center justify-between">
            <QuantitySelector
              quantity={quantity}
              onDecrease={() => onQuantityChange("decrease")}
              onIncrease={() => onQuantityChange("increase")}
              disabled={isQuantityUpdating}
              size="sm"
            />
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-900">{total_price ?? price}</div>
              {old_price && (
                <div className="text-xs text-gray-500 line-through">
                  {old_price}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 pt-2 border-t border-gray-100">
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-400 hover:text-red-500 transition-colors"
              onClick={onRemove}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Sil
            </Button>
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-between pt-2">
          <div className="flex items-center gap-4">
            <QuantitySelector
              quantity={quantity}
              onDecrease={() => onQuantityChange("decrease")}
              onIncrease={() => onQuantityChange("increase")}
              disabled={isQuantityUpdating}
            />
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                onClick={onRemove}
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-gray-900">{total_price ?? price}</div>
            {old_price && (
              <div className="text-sm text-gray-500 line-through">
                {old_price}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasketItem;